import React, {useEffect, useState} from 'react';
import {Col, Container, Row} from 'reactstrap';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

import {usePhaseState} from 'funnel-schedule/phase-context';
import {useUserState} from 'contexts/user';
import useBalanceText from 'utils/useBalanceText';
import useBeacon from 'utils/useBeacon';
import track from 'utils/track';

import PageMeta from 'components/PageMeta';
import SectionCL from 'components/ComponentLibrary/SectionCL';
import ScrollingVideo from 'components/ComponentLibrary/ScrollingVideo';
import CheckoutButton from 'components/CheckoutButton';
import CountdownTimer from 'components/Elements/CountdownTimer';
import {SalesDisclaimer} from 'components/SalesCta';
import SalesCopy from 'components/SalesCopy';
import ModalExitSales from 'components/Modals/ModalExitSales';
import Footer from 'components/Elements/Footer';

export default function SalesIndex({
  noExitIntent = false,
  noFixedVideo = false,
  ctaOverride = null,
  timerOverride = null,
  videoUrlOverride = null,
  ctaNoTimer = false,
  variant = null,
  bucketOverride = null,
  pageTitle = 'Food Revolution Network’s Plant-Powered Playbook',
  pageDescription = 'Make a difference with the food you eat',
  ogImage = 'https://cdn.foodrevolution.org/ppp/og/ppp-opengraph-sales-Health-motivated-1200x1200-v20221025.jpg',
  shareUrl = 'https://plantplaybook.foodrevolution.org/join/',
  facebookDescription = 'Want to make a difference in climate change, be kind to animals, and know you’re doing the best thing for your health — while eating delicious food? The Plant-Powered Playbook shows you how. Learn more here:',
  twitterDescription = 'Want to make a difference in climate change, be kind to animals, and know you’re doing the best thing for your health — while eating delicious food? The Plant-Powered Playbook shows you how. Learn more here:',
  twitterImg = 'https://cdn.foodrevolution.org/ppp/og/ppp-opengraph-sales-Health-motivated-1200x675-20221025.jpg',
  pinterestDescription = 'Want to make a difference in climate change, be kind to animals, and know you’re doing the best thing for your health — while eating delicious food? The Plant-Powered Playbook shows you how. Learn more here:',
  pinterestImage = 'https://cdn.foodrevolution.org/ppp/og/ppp-opengraph-sales-Health-motivated-1000x1500-20221025.jpg',
  ...props
}) {
  const {email, registrationId} = useUserState();
  const {
    phases: {
      default: defaultSchedule = {
        phase: 'before'
      },
      sales: salesSchedule = {
        phase: 'during'
      },
      webinar: webinarSchedule = {
        phase: 'before'
      }
    }
  } = usePhaseState();
  const {bucket: userBucket = []} = useUserState();
  const {report: webinarReport} = webinarSchedule;
  const userSegment = bucketOverride ?? userBucket ?? 'health';
  const segment = userSegment === 'default' ? 'health' : userSegment;

  const joinSalesPrice = 47;
  const fullPrice = 197;

  const isCartClose = new Date(webinarSchedule.endOfSalesDay * 1000 - 86400000).getTime() < new Date().getTime();
  const salesEnd = new Date(webinarSchedule.endOfSalesDay * 1000).getTime();
  const salesTimerDate = timerOverride ? timerOverride : salesEnd;

  const salesFullPrice =
    (!email && !registrationId && !ctaOverride) ||
    (!ctaOverride && webinarSchedule.phase === 'after') ||
    ctaOverride === 'joinFull';

  const productID = 'SC-PPP';
  const productSku = 'SC-PPP';
  const productName = 'Plant-Powered Playbook';
  const productSlug = !salesFullPrice ? 'ppp-47-special' : 'ppp';
  const productValue = !salesFullPrice ? 47 : 197;
  const paymentPlanPrice = !salesFullPrice ? 0 : 0;

  useEffect(() => {
    track('Product Viewed', {
      product_id: productID,
      sku: productSku,
      name: productName,
      variant: productSlug,
      value: productValue,
      currency: 'usd'
    });
  }, [salesFullPrice]);

  const videoUrl = videoUrlOverride
    ? videoUrlOverride
    : !salesFullPrice
    ? isCartClose
      ? {
          curious: 'https://www.youtube.com/watch?v=gUMxWu3NmyU',
          sustainability: 'https://www.youtube.com/watch?v=LwTsyzqYVN4',
          health: 'https://www.youtube.com/watch?v=Gf03kG7zRBc'
        }[segment]
      : {
          curious: 'https://www.youtube.com/watch?v=gUMxWu3NmyU',
          sustainability: 'https://www.youtube.com/watch?v=LwTsyzqYVN4',
          health: 'https://www.youtube.com/watch?v=Gf03kG7zRBc'
        }[segment]
    : {
        curious: 'https://www.youtube.com/watch?v=Ad1o4qghBV8',
        sustainability: 'https://www.youtube.com/watch?v=Ad1o4qghBV8',
        health: 'https://www.youtube.com/watch?v=Ad1o4qghBV8'
      }[segment];
  const videoThumbUrl = {
    curious: 'https://cdn.foodrevolution.org/ppp/videothumb/sc-ppp-thumb-curious.gif',
    sustainability: 'https://cdn.foodrevolution.org/ppp/videothumb/sc-ppp-thumb-sustainability.gif',
    health: 'https://cdn.foodrevolution.org/ppp/videothumb/sc-ppp-thumb-health.gif'
  }[segment];

  useBalanceText();
  useBeacon();
  return (
    <div className="page page-nosocial d-flex flex-column">
      <PageMeta title={pageTitle} description={pageDescription} url={shareUrl} ogImage={ogImage} />

      <div className="page-content">
        <SectionCL id="hbc-header" innerClass="text-white text-center" color="black" order="first" type="triangle">
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading mb-4">
                  {
                    {
                      curious: <>What’s so great about going plant-based?</>,
                      sustainability: <>How your food choices can help save the planet (for real!)</>,
                      health: <>How to feel awesome with a plant-based diet</>
                    }[segment]
                  }
                </h2>
              </Col>
            </Row>
            <Row className="d-flex justify-content-center">
              <Col lg="10" className="video-col px-0 px-sm-3">
                <h5 className="section-heading">Watch this video:</h5>
                <ScrollingVideo
                  video={{
                    url: videoUrl,
                    label: 'SC-PPP - Sales',
                    thumbnail: videoThumbUrl,
                    noButton: true,
                    playing: true
                  }}
                  text={
                    <h3 className="mt-0 mb-3">
                      Enroll in the groundbreaking <span className="text-nowrap">Plant-Powered</span> Playbook
                    </h3>
                  }
                  button={{
                    scrollToSection: 'offer-cta-3',
                    text: 'Buy Now',
                    optin: false
                  }}
                  noFixed={noFixedVideo}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <div className="header-cta-wrap mt-5">
                  {!salesFullPrice ? (
                    <>
                      <h2 className="section-heading mb-3">
                        Right Now Just <del>${fullPrice}</del> ${joinSalesPrice}
                      </h2>
                      {!ctaNoTimer ? (
                        <>
                          <h5 className="mt-4">Your discount expires in:</h5>
                          <CountdownTimer time={salesTimerDate} className="countdown-light" />
                        </>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <h2 className="section-heading">Right Now ${fullPrice}</h2>
                    </>
                  )}
                  <CheckoutButton sku={productSku} price={productValue} slug={productSlug} className="my-3" />
                  <SalesDisclaimer iconColor="white" />
                </div>
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <SalesCopy
          version="sales"
          productId={productID}
          productSku={productSku}
          productSlug={productSlug}
          productPrice={productValue}
          paymentPlanPrice={paymentPlanPrice}
          variant={variant}
          salesFullPrice={salesFullPrice}
          ctaOverride={ctaOverride}
          ctaNoTimer={ctaNoTimer}
          timerOverride={timerOverride}
          bucketOverride={bucketOverride}
        />
      </div>

      {noExitIntent || salesFullPrice ? null : <ModalExitSales />}

      <div className="footer-content mt-auto">
        <Footer />
      </div>
    </div>
  );
}
